import { useId, useState } from 'react'
import { Lock, Plus, X } from 'lucide-react'
import AutoComplete from '@/components/form/AutoComplete'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Typography } from '@/components/ui/typography'

export function TaxCollectionStates({
  value,
  onChange,
  stateOptions,
  activeLocationStates,
  allLocations = false,
  isReadOnly,
  errors = [],
}) {
  const id = useId()
  const [search, setSearch] = useState('')
  const [pendingState, setPendingState] = useState('')
  const physicalStates = new Map(activeLocationStates.map((state) => [state.state, state]))
  const selected = new Set([...value, ...physicalStates.keys()])
  const selectedStates = stateOptions
    .filter((state) => selected.has(state.id))
    .sort((a, b) => a.name.localeCompare(b.name))
  const availableStates = stateOptions.filter((state) => !selected.has(state.id))
  const items = availableStates
    .map((state) => ({ value: state.id, label: `${state.name} (${state.id})` }))
    .filter((state) => state.label.toLowerCase().includes(search.toLowerCase().trim()))
  const pendingOption = availableStates.find((state) => state.id === pendingState)
  const canAdd = pendingOption && search === `${pendingOption.name} (${pendingOption.id})`

  const addState = () => {
    if (!canAdd || isReadOnly) return
    onChange([...value, pendingState].sort())
    setPendingState('')
    setSearch('')
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>States where you collect sales tax</CardTitle>
        <CardDescription>
          Add states where your shop is required to collect sales tax. States with active locations
          are included automatically.
          {allLocations
            ? ' Changes here apply to all locations using automatic tax calculation.'
            : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {!isReadOnly ? (
          <div className="space-y-2">
            <Label htmlFor={id}>Add a state</Label>
            <div className="flex items-center gap-2">
              <AutoComplete
                id={id}
                selectedValue={pendingState}
                onSelectedValueChange={setPendingState}
                searchValue={search}
                onSearchValueChange={setSearch}
                items={items}
                placeholder="Search states"
                emptyMessage="No additional states found"
                disabled={availableStates.length === 0}
              />
              <Button type="button" disabled={!canAdd} onClick={addState}>
                <Plus /> Add
              </Button>
            </div>
          </div>
        ) : null}

        {selectedStates.length > 0 ? (
          <ul className="space-y-4" aria-label="Tax collection states">
            {selectedStates.map((state) => {
              const physical = physicalStates.get(state.id)

              return (
                <li key={state.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Typography as="span" variant="body" weight="semibold">
                        {state.name}
                      </Typography>
                      {physical ? (
                        <Badge variant="muted">
                          <Lock /> Active location
                        </Badge>
                      ) : null}
                    </div>
                    {physical ? (
                      <Typography variant="supporting" className="block">
                        {physical.locations.map((location) => location.name).join(', ')}
                      </Typography>
                    ) : null}
                  </div>
                  {!isReadOnly ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${state.name}`}
                      disabled={Boolean(physical)}
                      onClick={() => onChange(value.filter((code) => code !== state.id))}
                    >
                      <X />
                    </Button>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : (
          <Typography variant="supporting">No tax collection states selected.</Typography>
        )}

        {errors.length > 0 ? (
          <Typography variant="supporting" tone="destructive" role="alert">
            {errors.map((error) => (typeof error === 'string' ? error : error.message)).join(' ')}
          </Typography>
        ) : null}
        <Typography variant="caption" className="block">
          <Lock className="mr-1 inline-block size-3 align-[-0.125em]" aria-hidden="true" />
          States with active locations cannot be removed. Changes apply to new tax calculations;
          existing orders are unchanged.
        </Typography>
      </CardContent>
    </Card>
  )
}
